import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Import pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import StudentDashboard from "./pages/StudentDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ProfessorDashboard from "./pages/ProfessorDashboard";
import LoginPage from "./pages/LoginPage";
import VerifyMagicLink from "./pages/VerifyMagicLink";
import PacksStore from "./pages/PacksStore";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentPages";
import StudentProfile from "./pages/StudentProfile";
import ProfileSettings from "./pages/ProfileSettings";
import CheckinPage from "./pages/CheckinPage";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import { AppSettingsProvider } from "./context/AppSettingsContext";

// Remove trailing slash from BACKEND_URL to avoid double slashes
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Check sessionStorage first (more secure), then localStorage for backwards compatibility
      const storedToken = sessionStorage.getItem("session_token") || localStorage.getItem("session_token");
      const config = { withCredentials: true };
      if (storedToken) {
        config.headers = { Authorization: `Bearer ${storedToken}` };
        axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
        // Migrate from localStorage to sessionStorage if needed
        if (localStorage.getItem("session_token") && !sessionStorage.getItem("session_token")) {
          sessionStorage.setItem("session_token", storedToken);
          localStorage.removeItem("session_token");
        }
      }
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
    } catch (error) {
      // Only clear if there's no token in storage
      const currentToken = sessionStorage.getItem("session_token") || localStorage.getItem("session_token");
      if (!currentToken) {
        setUser(null);
        delete axios.defaults.headers.common["Authorization"];
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // Google auth removed - using magic link only
    window.location.href = "/login";
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      // Clear tokens from both storages for security
      sessionStorage.removeItem("session_token");
      localStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
      toast.success("Sessão terminada com sucesso!");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFCF6]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0F4C81] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#0F4C81] font-medium">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyMagicLink />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/packs"
        element={
          <ProtectedRoute>
            <PacksStore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/subscription-success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/settings"
        element={
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:userId"
        element={
          <ProtectedRoute>
            <StudentProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkin/:code"
        element={<CheckinPage />}
      />
      <Route
        path="/professor/*"
        element={
          <ProtectedRoute>
            <ProfessorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppSettingsProvider>
        <AuthProvider>
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: "'Manrope', sans-serif",
              },
            }}
          />
          <AppRouter />
        </AuthProvider>
      </AppSettingsProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
